// Map a Grandstream model string to a DECT/FXS device profile (#1156). Used when a
// discovered device is picked in the DECT/FXS add dialog: it auto-selects the device
// TYPE and pre-adds the right number of port/handset lines so the operator doesn't have
// to know that an HT802 has 2 analog ports.
//
// FXS (HandyTone ATA) port counts are encoded in the model number:
//   HT801=1  HT802=2  HT811=1  HT812=2  HT813=2 (1 FXS + 1 FXO)  HT814=4  HT818=8
// DECT bases (DP7xx/DP8xx) host cordless handsets — start with one line; the operator
// adds a line per handset they've paired.

export type DeviceType = 'fxs' | 'dect';
export interface DeviceProfile {
  type: DeviceType;
  ports: number;   // how many lines to pre-add
}

const HT_PORTS: Record<string, number> = {
  HT801: 1, HT802: 2, HT811: 1, HT812: 2, HT813: 2, HT814: 4, HT818: 8,
};

export function deviceProfileFromModel(model: string | null | undefined): DeviceProfile {
  const m = String(model || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const ht = m.match(/HT8\d\d/);                                          // HandyTone FXS ATA (may carry a vendor prefix)
  if (ht) return { type: 'fxs', ports: HT_PORTS[ht[0]] ?? 2 };
  if (/DP[78]\d/.test(m)) return { type: 'dect', ports: 1 };              // DECT base (DP7xx/DP8xx)
  return { type: 'fxs', ports: 1 };                                       // unknown → 1 line, editable
}

// Did discovery actually resolve a real model, or is it a placeholder? Scans often land a
// Grandstream device by MAC/OUI before its model is known, storing 'unknown' / '?' / blank.
// When the model is not known we must NOT show it as the model (that produced "ATA ?"), and
// we should run Identify to resolve it (so an HT802 gets its 2 ports).
export function isKnownModel(model: string | null | undefined): boolean {
  const m = String(model || '').trim().toLowerCase();
  if (!m) return false;
  if (['unknown', '?', '-', 'n/a', 'na', 'none', 'null'].includes(m)) return false;
  return /[a-z0-9]/.test(m);
}

// A serial number is ONLY needed to provision a drop-ship device — one with no LAN IP we
// can reach, where GDMS RPS (which hard-requires MAC+SN) is the only path. A device we can
// already reach on the LAN provisions over multicast PnP with no GDMS and no SN, so the SN
// field must not be shown (it read as required). Keyed on whether we have a reachable IP.
export function needsSerial(device: { ip?: string | null } | null | undefined): boolean {
  return !String((device && device.ip) || '').trim();
}

// Is this discovered row a plausible DECT/FXS candidate (or unknown Grandstream the
// operator may still want to add manually)? We surface Grandstream rows that are not
// already assigned; ATAs/bases often report model 'unknown' until Identify runs.
export function isDectFxsCandidate(row: { vendor_key?: string | null; status?: string | null; model?: string | null }): boolean {
  const vendor = String(row.vendor_key || '').toLowerCase();
  const assigned = String(row.status || '').toLowerCase() === 'assigned';
  return vendor === 'grandstream' && !assigned;
}
